<template lang="pug">
  .support-conversation-view(v-loading="loading")
    .support-page-header
      el-button(size="small" @click="goBack") ← Bandeja
      h1 Conversación
      .support-page-actions
        el-tag(v-if="conversation" :type="statusTagType(conversation.status)" size="small") {{ conversation.status }}
        el-button(v-if="canClose" size="small" type="primary" @click="closeDialogVisible = true") Cerrar conversación

    el-row(:gutter="16" v-if="conversation")
      //- Hilo de mensajes
      el-col(:span="16")
        .support-thread(ref="thread")
          .support-msg(
            v-for="m in messages"
            :key="m._id"
            :class="messageClass(m)"
          )
            .support-msg-avatar(v-if="m.sender === 'bot'") 🤖
            .support-msg-avatar(v-else-if="m.sender === 'human'") 👤
            .support-msg-avatar(v-else-if="m.sender === 'user'") 👤
            .support-msg-avatar(v-else) ⓘ
            .support-msg-bubble
              .support-msg-sender {{ senderLabel(m) }} · {{ formatTime(m.sentAt) }}
              .support-msg-text {{ m.text }}
              .support-msg-meta(v-if="m.llm")
                el-tag(size="mini") confianza {{ (m.llm.confidence * 100).toFixed(0) }}%
                el-tag(size="mini" v-for="(id, i) in (m.llm.kbArticleIds || [])" :key="i") KB:{{ String(id).slice(-6) }}

        .support-reply-box(v-if="canReply")
          el-input(
            type="textarea"
            v-model="replyDraft"
            :rows="3"
            placeholder="Escribí como soporte..."
          )
          .support-reply-actions
            el-button(size="small" @click="suggest" :loading="suggesting") 💡 Sugerir respuesta IA
            el-button(type="primary" size="small" :loading="sending" :disabled="!replyDraft.trim()" @click="send") Enviar
            el-button(v-if="!conversation.assignedTo" size="small" @click="takeConversation") Tomar conversación

      //- Panel contexto
      el-col(:span="8")
        el-card
          h3 Contexto
          .support-ctx
            div Usuario: {{ userDisplay(conversation.user) }}
            div Rol: {{ conversation.userRole }}
            div(v-if="conversation.context.route") Ruta: {{ conversation.context.route }}
            div(v-if="conversation.context.moduleHint") Módulo: {{ conversation.context.moduleHint }}
            div(v-if="conversation.context.lastErrors && conversation.context.lastErrors.length") Últimos errores:
              ul
                li(v-for="(e, i) in conversation.context.lastErrors" :key="i") {{ e }}
        el-card(style="margin-top: 16px" v-if="conversation.metrics")
          h3 Métricas
          .support-ctx
            div Mensajes: {{ conversation.metrics.messagesTotal || 0 }}
            div Bot: {{ conversation.metrics.botMessages || 0 }} · Humano: {{ conversation.metrics.humanMessages || 0 }}
            div(v-if="conversation.metrics.totalCostUsd > 0") Costo LLM: USD {{ conversation.metrics.totalCostUsd.toFixed(4) }}

    //- Cierre modal
    el-dialog(:visible.sync="closeDialogVisible" title="Cerrar conversación" width="500px")
      el-form(label-width="120px" size="small")
        el-form-item(label="Resultado")
          el-select(v-model="closeData.outcome" placeholder="Seleccionar")
            el-option(label="Resuelto por humano" value="solved_by_human")
            el-option(label="Resuelto por bot" value="solved_by_bot")
            el-option(label="No resuelto" value="unresolved")
            el-option(label="Bug del producto" value="product_bug")
            el-option(label="Spam" value="spam")
        el-form-item(label="Tags")
          el-input(v-model="closeTagsRaw" placeholder="cuenta-bloqueada, cambio-email")
        el-form-item(label="Notas internas")
          el-input(type="textarea" v-model="closeData.notes" :rows="3")
      div(slot="footer")
        el-button(@click="closeDialogVisible = false") Cancelar
        el-button(type="primary" :loading="closing" @click="closeConversation") Cerrar
</template>

<script>
import supportService from "@/services/support";

/**
 * @component SupportConversationView
 * @description Detalle de una conversación para el operador (Fase 5 —
 *              UC-REG-098). Hilo + contexto + caja de respuesta + sugerir
 *              IA + cerrar con outcome.
 *              Polling cada 3s.
 * @example <SupportConversationView />
 */
export default {
  name: "SupportConversationView",
  data() {
    return {
      loading: false,
      sending: false,
      suggesting: false,
      closing: false,
      conversation: null,
      messages: [],
      replyDraft: "",
      pollTimer: null,
      lastMessageId: null,
      closeDialogVisible: false,
      closeData: { outcome: "solved_by_human", tags: [], notes: "" },
      closeTagsRaw: "",
    };
  },
  computed: {
    canReply() {
      if (!this.conversation) return false;
      return ["waiting_human", "human_handling", "bot_handling"].includes(
        this.conversation.status
      );
    },
    canClose() {
      if (!this.conversation) return false;
      return this.conversation.status !== "resolved";
    },
  },
  mounted() {
    this.load();
    this.pollTimer = setInterval(this.poll, 3000);
  },
  beforeDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  },
  methods: {
    /**
     * @description Carga la conversación entera.
     */
    async load() {
      this.loading = true;
      try {
        const data = await supportService.getConversation(
          this.$route.params.id
        );
        this.conversation = data.conversation;
        this.messages = data.messages;
        if (this.messages.length) {
          this.lastMessageId = this.messages[this.messages.length - 1]._id;
        }
        this.scrollDown();
      } catch (err) {
        this.$message.error("No pude cargar la conversación.");
      } finally {
        this.loading = false;
      }
    },
    /**
     * @description Polling cada 3s.
     */
    async poll() {
      if (!this.conversation) return;
      try {
        const res = await supportService.pollSince(
          this.conversation._id,
          this.lastMessageId
        );
        if (res.messages && res.messages.length) {
          this.messages.push(...res.messages);
          this.lastMessageId = res.messages[res.messages.length - 1]._id;
          this.scrollDown();
        }
        if (res.status && res.status !== this.conversation.status) {
          this.conversation.status = res.status;
          this.conversation.assignedTo = res.assignedTo;
        }
      } catch (err) {
        /* silent */
      }
    },
    /**
     * @description Operador toma la conversación.
     */
    async takeConversation() {
      try {
        const updated = await supportService.assignConversation(
          this.conversation._id,
          "me"
        );
        this.conversation = updated;
        this.$message.success("Conversación asignada.");
      } catch (err) {
        this.$message.error("No pude tomarla.");
      }
    },
    /**
     * @description Pide sugerencia al LLM.
     */
    async suggest() {
      this.suggesting = true;
      try {
        const res = await supportService.suggestReply(this.conversation._id);
        if (res.suggestion) {
          this.replyDraft = res.suggestion;
        } else {
          this.$message.warning("El LLM no devolvió sugerencia.");
        }
      } catch (err) {
        this.$message.error("No pude generar la sugerencia.");
      } finally {
        this.suggesting = false;
      }
    },
    /**
     * @description Manda mensaje al usuario.
     */
    async send() {
      const text = this.replyDraft.trim();
      if (!text || this.sending) return;
      this.sending = true;
      try {
        await supportService.sendHumanMessage(this.conversation._id, text);
        this.replyDraft = "";
        await this.poll();
      } catch (err) {
        this.$message.error("No pude enviar.");
      } finally {
        this.sending = false;
      }
    },
    /**
     * @description Cierra con outcome + tags.
     */
    async closeConversation() {
      this.closing = true;
      try {
        const tags = this.closeTagsRaw
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean);
        const updated = await supportService.closeConversation(
          this.conversation._id,
          {
            ...this.closeData,
            tags,
          }
        );
        this.conversation = updated;
        this.closeDialogVisible = false;
        this.$message.success("Conversación cerrada.");
      } catch (err) {
        this.$message.error("No pude cerrar.");
      } finally {
        this.closing = false;
      }
    },
    /**
     * @description Clase para colorear el mensaje.
     * @param {Object} m
     */
    messageClass(m) {
      return {
        "is-user": m.sender === "user",
        "is-bot": m.sender === "bot",
        "is-human": m.sender === "human",
        "is-system": m.sender === "system",
      };
    },
    senderLabel(m) {
      if (m.sender === "bot") return "Bot";
      if (m.sender === "human") return "Soporte";
      if (m.sender === "system") return "Sistema";
      return "Usuario";
    },
    userDisplay(u) {
      if (!u) return "—";
      return `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.username;
    },
    formatTime(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      return d.toLocaleTimeString("es-AR", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    statusTagType(s) {
      if (s === "waiting_human") return "warning";
      if (s === "resolved") return "success";
      return "";
    },
    scrollDown() {
      this.$nextTick(() => {
        const el = this.$refs.thread;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    goBack() {
      this.$router.push({ name: "support-inbox" });
    },
  },
};
</script>

<style lang="scss" scoped>
.support-page-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  h1 {
    margin: 0;
    font-size: 22px;
    color: #002d5e;
    flex: 1;
  }
}
.support-page-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}
.support-thread {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  max-height: 500px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.support-msg {
  display: flex;
  gap: 8px;
  &.is-user {
    flex-direction: row-reverse;
  }
  &.is-user .support-msg-bubble {
    background: #002d5e;
    color: white;
    border-radius: 12px 12px 0 12px;
  }
  &.is-bot .support-msg-bubble,
  &.is-human .support-msg-bubble {
    background: #f0f2f7;
    color: #1a1f36;
    border-radius: 12px 12px 12px 0;
  }
  &.is-human .support-msg-bubble {
    background: #e8fdf8;
  }
  &.is-system .support-msg-bubble {
    background: #fff8e1;
    color: #6b5d24;
    font-style: italic;
    text-align: center;
  }
}
.support-msg-bubble {
  padding: 10px 14px;
  max-width: 75%;
  font-size: 14px;
}
.support-msg-sender {
  font-size: 11px;
  opacity: 0.7;
  margin-bottom: 4px;
}
.support-msg-text {
  white-space: pre-wrap;
  word-break: break-word;
}
.support-msg-meta {
  display: flex;
  gap: 4px;
  margin-top: 6px;
  flex-wrap: wrap;
}
.support-msg-avatar {
  font-size: 24px;
  flex-shrink: 0;
}
.support-reply-box {
  margin-top: 12px;
}
.support-reply-actions {
  display: flex;
  gap: 8px;
  margin-top: 8px;
  justify-content: flex-end;
}
.support-ctx {
  font-size: 13px;
  color: #6b7294;
  div {
    margin-bottom: 6px;
  }
  ul {
    margin: 4px 0;
    padding-left: 20px;
  }
}
</style>
